import { dist, type ElevFn, type Plan, type XY } from '@3dm/core';

const NS = 'http://www.w3.org/2000/svg';
const el = (tag: string, attrs: Record<string, string | number>) => {
  const e = document.createElementNS(NS, tag);
  for (const [k, v] of Object.entries(attrs)) e.setAttribute(k, String(v));
  return e;
};

export interface ProfilePt { xy: XY; h: number; role: string }

// Height profile along a path: flight height, terrain under the path sampled densely from the DEM, and
// terrain + floorM (the height the flight path must stay above: AGL on lines, min clearance on transit).
// `ref` (optional) is the un-thinned route over the same stretch: drawn dashed, with its dropped waypoints as
// hollow circles, so the effect of waypoint thinning is visible.
export function renderProfile(svg: SVGSVGElement, plan: Plan, pts: ProfilePt[], elev: ElevFn, floorM: number, ref?: ProfilePt[]) {
  svg.replaceChildren();
  const W = svg.clientWidth || 800, H = svg.clientHeight || 200;
  const pad = { l: 52, r: 12, t: 12, b: 24 };
  if (pts.length < 2) return;

  const cum = [0];
  for (let i = 1; i < pts.length; i++) cum.push(cum[i - 1] + dist(pts[i].xy, pts[i - 1].xy));
  const total = cum[cum.length - 1] || 1;
  // reference points: distance along the thinned path (projected onto the nearest leg, by cumulative distance)
  const refD: number[] = [];
  if (ref && ref.length > 1) {
    for (const p of ref) {
      let best = Infinity, d = 0;
      for (let i = 1; i < pts.length; i++) {
        const a = pts[i - 1].xy, b = pts[i].xy, L = cum[i] - cum[i - 1];
        if (L < 1e-6) continue;
        const f = Math.max(0, Math.min(1, ((p.xy[0] - a[0]) * (b[0] - a[0]) + (p.xy[1] - a[1]) * (b[1] - a[1])) / (L * L)));
        const dd = Math.hypot(p.xy[0] - (a[0] + f * (b[0] - a[0])), p.xy[1] - (a[1] + f * (b[1] - a[1])));
        if (dd < best) { best = dd; d = cum[i - 1] + f * L; }
      }
      refD.push(d);
    }
  }

  const terr: [number, number][] = [];
  for (let i = 1; i < pts.length; i++) {
    const a = pts[i - 1], b = pts[i], L = cum[i] - cum[i - 1];
    if (L < 1) continue;
    const n = Math.max(1, Math.ceil(L / 15));
    for (let k = 0; k <= n; k++) {
      const t = k / n;
      const [lon, lat] = plan.proj.inv(a.xy[0] + (b.xy[0] - a.xy[0]) * t, a.xy[1] + (b.xy[1] - a.xy[1]) * t);
      const h = elev(lon, lat);
      if (h != null && Number.isFinite(h)) terr.push([cum[i - 1] + L * t, h]);
    }
  }
  const hs = [...pts.map(w => w.h), ...terr.map(t => t[1]), ...(ref ?? []).map(w => w.h)];
  const hMin = Math.min(...hs) - 40, hMax = Math.max(...hs) + 40;
  const X = (d: number) => pad.l + (d / total) * (W - pad.l - pad.r);
  const Y = (h: number) => H - pad.b - ((h - hMin) / (hMax - hMin)) * (H - pad.t - pad.b);

  const step = [25, 50, 100, 200, 250, 500].find(s => (hMax - hMin) / s <= 6) ?? 1000;
  for (let h = Math.ceil(hMin / step) * step; h <= hMax; h += step) {
    svg.append(el('line', { x1: pad.l, x2: W - pad.r, y1: Y(h), y2: Y(h), class: 'grid' }));
    const t = el('text', { x: pad.l - 6, y: Y(h) + 4, 'text-anchor': 'end', class: 'axis' });
    t.textContent = `${h}`;
    svg.append(t);
  }
  const kmStep = total > 8000 ? 2000 : total > 3000 ? 1000 : total > 1200 ? 500 : 200;
  for (let d = 0; d <= total; d += kmStep) {
    const t = el('text', { x: X(d), y: H - 6, 'text-anchor': 'middle', class: 'axis' });
    t.textContent = `${(d / 1000).toFixed(1)} km`;
    svg.append(t);
  }

  if (terr.length) {
    const area = `M${X(terr[0][0])},${Y(hMin)} ` + terr.map(([d, h]) => `L${X(d)},${Y(h)}`).join(' ') + ` L${X(terr[terr.length - 1][0])},${Y(hMin)} Z`;
    svg.append(el('path', { d: area, class: 'terrain' }));
    svg.append(el('polyline', { points: terr.map(([d, h]) => `${X(d)},${Y(h + floorM)}`).join(' '), class: 'agl-floor' }));
  }
  if (ref && refD.length === ref.length && ref.length > pts.length) {
    svg.append(el('polyline', { points: ref.map((w, i) => `${X(refD[i])},${Y(w.h)}`).join(' '), class: 'flight-ref' }));
    const kept = new Set(pts.map(w => `${w.xy[0].toFixed(2)},${w.xy[1].toFixed(2)}`));
    ref.forEach((w, i) => { if (!kept.has(`${w.xy[0].toFixed(2)},${w.xy[1].toFixed(2)}`)) svg.append(el('circle', { cx: X(refD[i]), cy: Y(w.h), r: 2, class: 'wp dropped' })); });
  }
  svg.append(el('polyline', { points: pts.map((w, i) => `${X(cum[i])},${Y(w.h)}`).join(' '), class: 'flight' }));
  pts.forEach((w, i) => svg.append(el('circle', { cx: X(cum[i]), cy: Y(w.h), r: w.role === 'line' ? 2.5 : 3.5, class: 'wp ' + w.role })));
}
